import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { AuthService } from './services/auth-service.service';
import { filter, take, map, switchMap, tap, catchError } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { ConfigService } from './services/config.service';
import { AdminService } from './services/admin.service';
import { MyAccess } from './models/my-access';

const REQUIRE_AUTHZ_FEATURES = 'Dashboard:RequireAuthZ';
const DASHBOARD_FEATURES = new Set<string>([
  'DISDashboard',
  'DISDashboardAdmin'
]);

const isAccessAllowed = (cachedAccess: MyAccess | null): boolean => {
  if (!cachedAccess) return false;

  const flagEnabled = cachedAccess.flags.some(f => f.name === REQUIRE_AUTHZ_FEATURES && f.enabled);
  const allowed = !flagEnabled || cachedAccess.userFeatures.some(f => DASHBOARD_FEATURES.has(f));

  return allowed;
};

export const authGuard: CanMatchFn = () => {
  const auth = inject(AuthService);
  const config = inject(ConfigService);
  const adminService = inject(AdminService);
  const router = inject(Router);

  return config.$configuration.pipe(
    filter(cfg => Object.keys(cfg || {}).length > 0),
    take(1),
    switchMap(() => from(auth.loadUserData())),
    tap(isSignedIn => {
      if (!isSignedIn) {
        void auth.signIn().catch(error => {
          console.error('AuthGuard: sign-in failed', error);
        });
      }
    }),
    switchMap(isSignedIn => {
      if (!isSignedIn) return of(false);

      // Use cached access if present
      const cachedAccess = adminService.myAccess;
      if (cachedAccess) {
        const allowed = isAccessAllowed(cachedAccess);
        return of(allowed ? true : router.createUrlTree(['/unauthorized']));
      }

      return from(adminService.loadMyAccess()).pipe(
        map(() => {
          const allowed = isAccessAllowed(adminService.myAccess);
          return allowed ? true : router.createUrlTree(['/unauthorized']);
        }),
        catchError(() => of(router.createUrlTree(['/unauthorized'])))
      );
    }),
    // If anything else blows up, also treat as unauthorized
    catchError(() => of(router.createUrlTree(['/unauthorized'])))
  );
};